/**
 * File: stages.ts
 * Description: Shared stage definitions and utilities for the application
 * Author: System
 * Created: 2025-08-18
 * Last Modified: 2025-08-18
 */

export interface StageDefinition {
  id: number;
  name: string;
  label: string;
  description: string;
}

const TOTAL_STAGES = 8;

/**
 * Core stage definitions - single source of truth for all stage information.
 * Stage 2.5 (brownfield-conversion) is only displayed for brownfield projects.
 */
export const STAGE_DEFINITIONS: Record<number, StageDefinition> = {
  1: {
    id: 1,
    name: 'setup',
    label: 'Project Setup',
    description: 'Project metadata and configuration',
  },
  2: {
    id: 2,
    name: 'context',
    label: 'User Input',
    description: 'Upload and organize project documents',
  },
  2.5: {
    id: 2.5,
    name: 'brownfield-conversion',
    label: 'SAHLD Conversion',
    description: 'Convert existing SAHLD into Archie format',
  },
  3: {
    id: 3,
    name: 'cra',
    label: 'Context Review',
    description: 'AI-powered analysis and validation',
  },
  4: {
    id: 4,
    name: 'adrs',
    label: 'ADRs',
    description: 'Architecture Decision Records',
  },
  5: {
    id: 5,
    name: 'generation',
    label: 'Architecture Analysis',
    description: 'Create architecture sections',
  },
  6: {
    id: 6,
    name: 'c4',
    label: 'Bizzdesign Integration',
    description: 'Export C4 and technology architecture models',
  },
  7: {
    id: 7,
    name: 'review',
    label: 'Final Review',
    description: 'Complete document review and export',
  },
};

export const CONTEXT_MANAGEMENT_LABEL = STAGE_DEFINITIONS[2].label;

/**
 * Get stage label by stage number
 */
export const getStageLabel = (stageNumber: number | null): string => {
  if (!stageNumber || !STAGE_DEFINITIONS[stageNumber]) {
    return 'Architecture Assistant';
  }
  return STAGE_DEFINITIONS[stageNumber].label;
};

/**
 * Get stage description by stage number
 */
export const getStageDescription = (stageNumber: number | null): string => {
  if (!stageNumber || !STAGE_DEFINITIONS[stageNumber]) {
    return 'General architecture assistance';
  }
  return STAGE_DEFINITIONS[stageNumber].description;
};

/**
 * Get stage name by stage number
 */
export const getStageName = (stageNumber: number | null): string => {
  if (!stageNumber || !STAGE_DEFINITIONS[stageNumber]) {
    return 'general';
  }
  return STAGE_DEFINITIONS[stageNumber].name;
};

/**
 * Get all stage definitions as an array
 */
export const getAllStages = (): StageDefinition[] => {
  return Object.values(STAGE_DEFINITIONS);
};

/**
 * Check if a stage number is valid
 */
export const isValidStage = (stageNumber: number): boolean => {
  return (
    stageNumber >= 1 &&
    stageNumber <= TOTAL_STAGES &&
    STAGE_DEFINITIONS[stageNumber] !== undefined
  );
};
